import { inject, Injectable, isDevMode } from '@angular/core';
import {
  type ApolloClient,
  ApolloLink,
  InMemoryCache,
  type InMemoryCacheConfig,
  ServerError,
  type TypePolicies,
} from '@apollo/client';
import { CombinedGraphQLErrors, CombinedProtocolErrors } from '@apollo/client/errors';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { AUTH_TOKEN_SERVICE, AuthApiService } from '@reconcept-platform/shared/authentication';
import { getLanguageCode } from '@reconcept-platform/shared/util';
import { HttpLink } from 'apollo-angular/http';
import { OperationTypeNode } from 'graphql/language';
import { from, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { BACKEND_VERSION } from '../utils/backend-version';
import { ApolloGraphqlErrorService } from './apollo-graphql-error.service';
import { extendUri } from './apollo-graphql.utils';
import './apollo-types';
import { GRAPHQL_SUBSCRIPTIONS_LINK } from './graphql-subscriptions-link.token';

@Injectable({
  providedIn: 'root',
})
export class ApolloGraphqlConfigService {
  protected httpLink = inject(HttpLink);
  protected authApi = inject(AuthApiService);
  protected authTokens = inject(AUTH_TOKEN_SERVICE);
  protected apolloErrors = inject(ApolloGraphqlErrorService);
  private subscriptionsLink = inject(GRAPHQL_SUBSCRIPTIONS_LINK, { optional: true });

  create(
    name: string,
    uri: string,
    introspectionQueryResultData?: any,
    typePolicies?: TypePolicies,
  ): ApolloClient.Options {
    const httpLink = this.httpLink.create({
      uri: isDevMode() ? extendUri(uri) : uri,
    });

    const errorLink = this.createErrorLink(this);
    const errorsFromDataToErrorLink = this.createErrorsFromDataToError();
    const authLink = this.createAuthLink();
    const languageLink = this.createLanguageLink();
    const config = this.createConfig(introspectionQueryResultData, typePolicies);
    const backendVersion = this.readBackendVersion();

    const cache = new InMemoryCache(config);

    return {
      link: this.withSubscriptions(
        ApolloLink.from([backendVersion, errorLink, errorsFromDataToErrorLink, authLink, languageLink, httpLink]),
      ),
      cache: cache,
      clientAwareness: {
        name,
      },
      defaultOptions: {
        watchQuery: {
          errorPolicy: 'all',
          notifyOnNetworkStatusChange: false,
        },
        query: {
          errorPolicy: 'all',
        },
      },
    };
  }

  // subscriptions bypass the http links: the websocket authenticates itself when it connects,
  // and an app without the websocket gets no live updates, /graphql can't serve them
  protected withSubscriptions(httpLinks: ApolloLink) {
    return ApolloLink.split(
      (operation) => operation.operationType === OperationTypeNode.SUBSCRIPTION,
      this.subscriptionsLink ?? ApolloLink.empty(),
      httpLinks,
    );
  }

  protected readBackendVersion() {
    return new ApolloLink((operation, forward) => {
      return forward(operation).pipe(
        map((response) => {
          const context = operation.getContext();
          const {
            response: { headers },
          } = context;

          if (headers) {
            const version = headers.get('Rcp-Backend-Version');
            BACKEND_VERSION.set(version);
          }

          return response;
        }),
      );
    });
  }

  protected createConfig(introspectionQueryResultData?: any, typePolicies?: TypePolicies) {
    const config = {} as InMemoryCacheConfig;

    if (introspectionQueryResultData) {
      const possibleTypes = {};
      introspectionQueryResultData.__schema.types.forEach((supertype: any) => {
        if (supertype.possibleTypes) {
          (possibleTypes as any)[supertype.name] = supertype.possibleTypes.map((subtype: any) => subtype.name);
        }
      });

      config.possibleTypes = possibleTypes;
    }

    config.typePolicies = typePolicies;

    return config;
  }

  protected createAuthLink() {
    return new SetContextLink(async (prevContext) => {
      const hasAuthTokens = await this.authTokens.hasAuthToken();
      const tokens = await this.authTokens.retrieveAccessToken();
      return {
        headers: {
          ...prevContext.headers,
          ...(hasAuthTokens ? { Authorization: `Bearer ${tokens}` } : {}),
        },
      };
    });
  }

  protected createLanguageLink() {
    return new SetContextLink(async (prevContext) => {
      return {
        headers: {
          ...prevContext.headers,
          'x-reconcept-language': getLanguageCode(),
        },
      };
    });
  }

  protected createErrorsFromDataToError() {
    return new ApolloLink((operation, forward) => {
      return forward(operation).pipe(
        map((response) => {
          if (response.errors) {
            return response;
          }

          // @ts-expect-error TS2769 no overload matches
          for (const key of Object.keys(response.data)) {
            // @ts-expect-error TS18049 possibly null or undefined
            const responseObject = response.data[key];
            if (responseObject?.errors?.length) {
              // if errors found, add it to root of the response,
              // so it's handled by the default error handling
              return {
                ...response,
                errors: responseObject.errors,
              };
            }
          }
          return response;
        }),
      );
    });
  }

  protected createErrorLink(self: ApolloGraphqlConfigService): ApolloLink {
    // Log any GraphQL errors, protocol errors, or network error that occurred
    return new ErrorLink(({ error, operation, forward }) => {
      // this should be the default way to hande 401
      if (ServerError.is(error) && error.statusCode === 401) {
        return this.updateTokenAndRetry(self, operation, forward);
      }

      // we keep this as fallback
      if (error.name === 'HttpErrorResponse') {
        if ('status' in error && error.status === 401) {
          return this.updateTokenAndRetry(self, operation, forward);
        }
      }

      if (CombinedGraphQLErrors.is(error)) {
        if (operation.operationType === OperationTypeNode.MUTATION) {
          // @ts-expect-error TS2345 argument not assignable
          this.apolloErrors.processMutation(error.errors, operation.operationName, operation.variables);
        } else {
          // @ts-expect-error TS2345 argument not assignable
          this.apolloErrors.processQuery(error.errors, operation.operationName, operation.variables);
        }
      } else if (CombinedProtocolErrors.is(error)) {
        error.errors.forEach((formattedError) => this.apolloErrors.processApolloNetworkError(formattedError));
      }

      return undefined;
    });
  }

  // called from 'function'-scope, this does not refer to class instance.
  // use self
  private updateTokenAndRetry(
    self: ApolloGraphqlConfigService,
    operation: ApolloLink.Operation,
    forward: ApolloLink.ForwardFunction,
  ) {
    return from(self.authTokens.retrieveRefreshToken()).pipe(
      switchMap((refreshToken) => from(self.authApi.refreshAccessToken(refreshToken))),
      // @ts-expect-error TS2345 argument not assignable
      switchMap((tokens) => from(self.authTokens.setTokens(tokens))),
      switchMap(() => self.updateHeadersWithNewToken(self, operation, forward)),
    );
  }

  // called from 'function'-scope, this does not refer to class instance.
  // use self
  private updateHeadersWithNewToken(
    self: ApolloGraphqlConfigService,
    operation: ApolloLink.Operation,
    forward: ApolloLink.ForwardFunction,
  ) {
    return from(self.authTokens.retrieveAccessToken()).pipe(
      map((accessToken) => {
        operation.setContext((previousContext) => ({
          headers: {
            ...previousContext.headers,
            Authorization: `Bearer ${accessToken}`,
          },
        }));

        return operation;
      }),
      switchMap((updatedOperation) => forward(updatedOperation)),
    );
  }
}
